import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { HeartHandshake, Menu, X, LogOut, Heart, LogIn, Newspaper } from "lucide-react";
import { useLang, authTranslations } from "../i18n";
import { useAuth } from "../auth";
import { LanguageSelector } from "./LanguageSelector";

const NAV_ITEMS = [
  { id: "home", key: "home" },
  { id: "chi-siamo", key: "about" },
  { id: "attivita", key: "activities" },
  { id: "cosa-puoi-fare", key: "help" },
  { id: "volontariato", key: "volunteering", route: "/volontariato" },
  { id: "news", key: "news" },
  { id: "contatti", key: "contact" },
];

export const Logo = ({ onClick }) => (
  <button
    data-testid="logo-button"
    onClick={onClick}
    className="flex items-center gap-3 outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-full"
  >
    <span className="grid h-10 w-10 place-items-center rounded-full bg-primary text-white shadow-lg shadow-primary/30">
      <HeartHandshake size={20} strokeWidth={2.2} />
    </span>
    <span className="font-serif text-[26px] leading-none tracking-tight text-white">
      You<span className="italic text-primary">4</span>Mada
    </span>
  </button>
);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const googleLogin = () => {
  const redirectUrl = window.location.origin + "/area-donatore";
  const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  // OAuth page cannot load inside a sandboxed iframe preview: open a new tab
  try {
    if (window.top && window.top !== window.self) {
      const w = window.open(authUrl, "_blank");
      if (!w) window.top.location.href = authUrl;
    } else {
      window.location.href = authUrl;
    }
  } catch {
    window.location.href = authUrl;
  }
};

function UserMenu({ onNavigate }) {
  const { lang } = useLang();
  const a = authTranslations[lang];
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        data-testid="user-menu-button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-full border border-white/15 py-1 pl-1 pr-4 transition-colors duration-300 hover:border-white/40"
      >
        {user.picture ? (
          <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          <span className="grid h-8 w-8 place-items-center rounded-full bg-primary text-sm font-semibold text-white">
            {(user.name || user.email || "U")[0].toUpperCase()}
          </span>
        )}
        <span className="max-w-[110px] truncate text-sm font-medium text-white">
          {(user.name || "").split(" ")[0] || user.email}
        </span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.97 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 top-12 w-56 overflow-hidden rounded-2xl border border-white/10 bg-[#071d40] p-2 shadow-2xl"
            data-testid="user-menu-dropdown"
          >
            <button
              data-testid="user-menu-donations"
              onClick={() => {
                setOpen(false);
                onNavigate("/area-donatore");
              }}
              className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm text-white/80 transition-colors hover:bg-white/10 hover:text-white"
            >
              <Heart size={16} className="text-primary" />
              {a.myDonations}
            </button>
            {user.is_admin && (
              <button
                data-testid="user-menu-admin"
                onClick={() => {
                  setOpen(false);
                  onNavigate("/admin");
                }}
                className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              >
                <Newspaper size={16} className="text-primary" />
                Gestione news
              </button>
            )}
            <button
              data-testid="user-menu-logout"
              onClick={() => {
                setOpen(false);
                logout();
              }}
              className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm text-white/80 transition-colors hover:bg-white/10 hover:text-white"
            >
              <LogOut size={16} className="text-primary" />
              {a.logout}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Header({ scrollTo, onDonate }) {
  const { lang, setLang, t } = useLang();
  const a = authTranslations[lang];
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const go = (item) => {
    setOpen(false);
    if (item.route) navigate(item.route);
    else scrollTo(item.id);
  };

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#04122e]/70 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 md:px-8">
        <Logo onClick={() => go({ id: "home" })} />

        <nav className="mx-6 hidden items-center gap-5 lg:flex" aria-label="Main">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-link-${item.id}`}
              onClick={() => go(item)}
              className="whitespace-nowrap text-[12px] font-medium uppercase tracking-[0.1em] text-white/70 transition-colors duration-300 hover:text-white"
            >
              {t.nav[item.key]}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <LanguageSelector />
          {user ? (
            <UserMenu onNavigate={navigate} />
          ) : (
            <button
              data-testid="login-button"
              onClick={googleLogin}
              className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-[13px] font-semibold uppercase tracking-[0.12em] text-white transition-all duration-300 hover:border-white/50 hover:bg-white/5"
            >
              <LogIn size={14} />
              {a.login}
            </button>
          )}
          <button
            data-testid="header-donate-button"
            onClick={onDonate}
            className="whitespace-nowrap rounded-full bg-primary px-6 py-2.5 text-[13px] font-semibold uppercase tracking-[0.12em] text-white shadow-lg shadow-primary/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#e0562a] focus-visible:ring-2 focus-visible:ring-white"
          >
            {t.nav.donate}
          </button>
        </div>

        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white lg:hidden"
          aria-label="Menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-white/10 bg-[#04122e]/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  data-testid={`mobile-nav-link-${item.id}`}
                  onClick={() => go(item)}
                  className="py-3 text-left font-serif text-2xl text-white/85 transition-colors hover:text-primary"
                >
                  {t.nav[item.key]}
                </button>
              ))}
              {user && (
                <button
                  data-testid="mobile-nav-donations"
                  onClick={() => {
                    setOpen(false);
                    navigate("/area-donatore");
                  }}
                  className="py-3 text-left font-serif text-2xl italic text-primary"
                >
                  {a.myDonations}
                </button>
              )}
              {user?.is_admin && (
                <button
                  data-testid="mobile-nav-admin"
                  onClick={() => {
                    setOpen(false);
                    navigate("/admin");
                  }}
                  className="py-3 text-left font-serif text-2xl italic text-primary"
                >
                  Gestione news
                </button>
              )}
              <div className="mt-4 flex items-center gap-4">
                <div className="flex items-center rounded-full border border-white/15 p-1">
                  {["it", "en"].map((l) => (
                    <button
                      key={l}
                      data-testid={`mobile-lang-switch-${l}`}
                      onClick={() => setLang(l)}
                      className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-widest ${
                        lang === l ? "bg-white text-[#04122e]" : "text-white/60"
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
                {user ? (
                  <button
                    data-testid="mobile-logout-button"
                    onClick={() => {
                      setOpen(false);
                      logout();
                    }}
                    className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-white"
                  >
                    <LogOut size={14} />
                    {a.logout}
                  </button>
                ) : (
                  <button
                    data-testid="mobile-login-button"
                    onClick={googleLogin}
                    className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-white"
                  >
                    <LogIn size={14} />
                    {a.login}
                  </button>
                )}
                <button
                  data-testid="mobile-donate-button"
                  onClick={() => {
                    setOpen(false);
                    onDonate();
                  }}
                  className="flex-1 rounded-full bg-primary px-6 py-3 text-[13px] font-semibold uppercase tracking-[0.12em] text-white"
                >
                  {t.nav.donate}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
